import React, { useEffect, useRef, useState } from "react";

/**
 * MixerChannel — one vertical audio-console strip.
 *
 * Displays a channel label, a stereo VU meter, a vertical gain fader, Mute +
 * Solo pads, and an optional mini 3-band EQ row. Pure-presentational: it
 * reports every change via the provided callbacks; the parent owns the state.
 *
 * Designed to mirror the look of a broadcast desk (vMix / X-Air / DiGiCo):
 * dark aluminum well, thin amber/green/red LED VU, fat plastic fader cap, red
 * Mute pad and yellow Solo pad beneath.
 */
export default function MixerChannel({
  label,
  level = 0,           // 0..1 — drives the VU meter
  volume = 1,          // 0..1.2 — fader position
  onVolumeChange,
  muted = false,
  onMuteToggle,
  soloed = false,
  onSoloToggle,
  eq,                  // { low, mid, high } — optional
  onEqChange,          // (next_eq) => void
  testidPrefix = "mixer-channel",
  color = "emerald",   // emerald | sky | amber | violet | rose — fader cap tint
  master = false,
}) {
  const vuCells = 16;
  const capTint = {
    emerald: "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]",
    sky: "bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.8)]",
    amber: "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.8)]",
    violet: "bg-violet-400 shadow-[0_0_8px_rgba(167,139,250,0.8)]",
    rose: "bg-rose-400 shadow-[0_0_8px_rgba(251,113,133,0.8)]",
    yellow: "bg-tv-accent shadow-[0_0_10px_rgba(255,212,0,0.9)]",
  }[color] || "bg-emerald-400";

  return (
    <div
      data-testid={`${testidPrefix}-col`}
      className={`flex flex-col items-center gap-1 px-1 py-1.5 rounded-md border ${master ? "bg-gradient-to-b from-[#1a1f2a] to-[#0e1119] border-tv-accent/40" : "bg-black/40 border-white/10"} w-full min-w-0`}
    >
      <div className={`text-[9px] uppercase tracking-wider font-black truncate max-w-full ${master ? "text-tv-accent" : "text-white/70"}`}>{label}</div>

      {/* VU + fader side by side */}
      <div className="flex items-end gap-0.5 h-28">
        {/* VU */}
        <div className="flex flex-col-reverse gap-[2px]" data-testid={`${testidPrefix}-vu`}>
          {Array.from({ length: vuCells }).map((_, i) => {
            const t = (i + 1) / vuCells;
            const on = level >= t;
            const c = t > 0.85 ? "bg-red-500" : t > 0.65 ? "bg-yellow-400" : "bg-emerald-400";
            return (
              <div
                key={i}
                className={`w-[5px] h-[7px] rounded-[1px] ${on ? c : "bg-white/10"}`}
                style={{ opacity: on ? 1 : 0.3 }}
              />
            );
          })}
        </div>
        {/* Fader rail */}
        <div className="relative h-28 w-5 rounded-sm bg-black/60 border border-white/10 flex items-center justify-center">
          <div className="absolute inset-y-2 w-[2px] bg-white/15 rounded-full" />
          {/* Tick marks */}
          {[0, 25, 50, 75, 100].map((p) => (
            <div key={p} className="absolute left-0 w-1 h-[1px] bg-white/20" style={{ top: `${p}%` }} />
          ))}
          <input
            type="range"
            min="0"
            max="1.2"
            step="0.01"
            value={volume}
            onChange={(e) => onVolumeChange?.(parseFloat(e.target.value))}
            data-testid={`${testidPrefix}-fader`}
            aria-label={`${label} fader`}
            className="absolute inset-0 m-0 appearance-none bg-transparent cursor-pointer"
            style={{
              writingMode: "bt-lr", // legacy IE/old FF
              WebkitAppearance: "slider-vertical",
              orient: "vertical",
              width: "100%",
              height: "100%",
              opacity: 0,
            }}
          />
          {/* The actual fader cap, positioned based on volume */}
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-[28px] h-[12px] rounded-[3px] ${capTint} pointer-events-none`}
            style={{ top: `${Math.max(2, Math.min(97, (1 - volume / 1.2) * 100))}%`, transform: "translate(-50%, -50%)" }}
          />
        </div>
      </div>

      {/* dB label */}
      <div className="text-[9px] font-bold text-white/50 tabular-nums">{(volume > 0 ? (20 * Math.log10(volume)).toFixed(1) : "-∞")} dB</div>

      {/* MUTE + SOLO pads */}
      <div className="flex gap-1">
        <button
          type="button"
          onClick={onMuteToggle}
          aria-pressed={muted}
          data-testid={`${testidPrefix}-mute`}
          className={`text-[9px] font-black px-2 py-1 rounded ${muted ? "bg-red-500 text-white shadow-[0_0_8px_rgba(239,68,68,0.7)]" : "bg-red-500/20 text-red-200 hover:bg-red-500/40"}`}
          title="Mute"
        >M</button>
        <button
          type="button"
          onClick={onSoloToggle}
          aria-pressed={soloed}
          data-testid={`${testidPrefix}-solo`}
          className={`text-[9px] font-black px-2 py-1 rounded ${soloed ? "bg-tv-accent text-black shadow-[0_0_8px_rgba(255,212,0,0.7)]" : "bg-yellow-400/15 text-yellow-200 hover:bg-yellow-400/30"}`}
          title="Solo"
        >S</button>
      </div>

      {/* Mini EQ knobs (optional) */}
      {eq && onEqChange && (
        <div className="flex gap-1 mt-0.5">
          {["low", "mid", "high"].map((band) => (
            <EqTinyKnob
              key={band}
              label={band[0].toUpperCase()}
              value={eq[band] || 0}
              onChange={(v) => onEqChange({ ...eq, [band]: v })}
              testid={`${testidPrefix}-eq-${band}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EqTinyKnob({ label, value, onChange, testid }) {
  // 0..100deg rotation representing -12..+12 dB
  const deg = (value / 12) * 135; // -135..+135
  const ref = useRef(null);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startVal = useRef(0);

  const onDown = (e) => {
    dragging.current = true;
    startY.current = e.clientY || e.touches?.[0]?.clientY || 0;
    startVal.current = value;
    e.preventDefault();
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    const y = e.clientY || e.touches?.[0]?.clientY || 0;
    const delta = startY.current - y; // up = positive
    const next = Math.max(-12, Math.min(12, startVal.current + delta * 0.2));
    onChange(next);
  };
  const onUp = () => { dragging.current = false; };

  useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line
  }, [value]);

  return (
    <div className="flex flex-col items-center gap-0.5">
      <div
        ref={ref}
        onMouseDown={onDown}
        onTouchStart={onDown}
        onDoubleClick={() => onChange(0)}
        data-testid={testid}
        className="w-5 h-5 rounded-full bg-gradient-to-b from-[#2a3140] to-[#0f1219] border border-white/20 relative cursor-ns-resize hover:border-tv-accent/60"
        title={`${label}: ${value > 0 ? "+" : ""}${value.toFixed(1)}dB (drag vertically, double-click to reset)`}
      >
        <div
          className="absolute left-1/2 top-1 w-[1.5px] h-2 bg-tv-accent rounded origin-top"
          style={{ transform: `translateX(-50%) rotate(${deg}deg)` }}
        />
      </div>
      <div className="text-[7px] font-bold text-white/40 uppercase">{label}</div>
    </div>
  );
}
